import { useState, useRef, useEffect } from "react";
import { Camera, Upload, Loader2, Sparkles, X, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { photoEstimate } from "@/lib/api";
import { formatPrice, SERVICES, VEHICLES } from "@/lib/constants";

const serviceLabel = (key) =>
    SERVICES.find((s) => s.key === key)?.label || key;

export default function PhotoEstimator() {
    const [photo, setPhoto] = useState(null); // {b64, mime, preview}
    const [vehicleType, setVehicleType] = useState("sedan");
    const [notes, setNotes] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const inputRef = useRef(null);

    const handleFile = (file) => {
        if (!file) return;
        if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
            toast.error("Please upload a JPG, PNG, or WEBP image.");
            return;
        }
        if (file.size > 6 * 1024 * 1024) {
            toast.error("Image must be under 6 MB.");
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = String(reader.result);
            const b64 = dataUrl.split(",")[1];
            setPhoto({ b64, mime: file.type, preview: dataUrl });
            setResult(null);
        };
        reader.readAsDataURL(file);
    };

    // Allow the Hero (or anywhere) to hand a selected photo straight to the estimator.
    useEffect(() => {
        const onPhoto = (e) => {
            const file = e?.detail?.file;
            if (!file) return;
            handleFile(file);
            document
                .getElementById("photo-estimator")
                ?.scrollIntoView({ behavior: "smooth" });
        };
        window.addEventListener("pp:photo", onPhoto);
        return () => window.removeEventListener("pp:photo", onPhoto);
    }, []);

    const run = async () => {
        if (!photo) {
            toast.error("Add a photo first.");
            return;
        }
        setLoading(true);
        try {
            const data = await photoEstimate({
                photo_base64: photo.b64,
                photo_mime: photo.mime,
                vehicle_type: vehicleType,
                notes,
            });
            setResult(data);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    "Couldn't analyze that photo. Try a clearer one.",
            );
        } finally {
            setLoading(false);
        }
    };

    const reset = () => {
        setPhoto(null);
        setResult(null);
        setNotes("");
    };

    const startFullQuote = () => {
        // Stash the photo + suggestions so QuoteEngine can pick up
        if (photo && result) {
            sessionStorage.setItem(
                "pp_prefill",
                JSON.stringify({
                    photo_base64: photo.b64,
                    photo_mime: photo.mime,
                    photo_preview: photo.preview,
                    services: result.detected_services,
                    damage_size: result.detected_damage_size,
                    vehicle_type: vehicleType,
                    description: notes,
                }),
            );
            window.dispatchEvent(new Event("pp:prefill"));
        }
        document.getElementById("quote")?.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <section
            id="photo-estimator"
            data-testid="photo-estimator-section"
            className="relative py-20 md:py-28 bg-carbon overflow-hidden"
        >
            <div className="absolute inset-0 ai-grid pointer-events-none" aria-hidden />
            <div className="absolute -top-24 -right-24 w-96 h-96 bg-electric/10 rounded-full blur-[130px] pointer-events-none" />
            <div className="absolute bottom-0 -left-24 w-96 h-96 bg-safety/10 rounded-full blur-[130px] pointer-events-none" />
            <div className="relative max-w-6xl mx-auto px-6 md:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-5">
                        <span
                            className="inline-flex items-center gap-2 glass text-electric text-xs uppercase tracking-[0.18em] rounded-full px-3.5 py-1.5"
                            data-testid="photo-estimator-badge"
                        >
                            <span className="w-2 h-2 rounded-full bg-electric pulse-dot" />
                            Claude Vision Powered AI
                        </span>
                        <h2 className="font-display text-4xl md:text-5xl mt-6 font-bold leading-[1.05] tracking-tight">
                            <span className="gradient-text">Snap a photo.</span>
                            <br />
                            <span className="text-safety">
                                Get a price in 15 seconds.
                            </span>
                        </h2>
                        <p className="mt-5 text-slate-400 leading-relaxed text-lg max-w-md">
                            No forms, no fuss. Upload a clear photo of the damage
                            and our AI will tell you what it sees, what
                            it&rsquo;ll likely cost, and what to do next.
                            Totally free, no obligation.
                        </p>
                        <ul className="mt-6 space-y-2.5 text-slate-400 text-sm">
                            {[
                                "Works on dents, scratches, bumper damage, full body repairs",
                                "Best with a clear, well-lit photo of the damage",
                                "We’ll never share your photo",
                            ].map((t) => (
                                <li
                                    key={t}
                                    className="flex gap-2.5 items-start"
                                >
                                    <Check
                                        size={16}
                                        className="text-electric mt-0.5 shrink-0"
                                    />
                                    <span>{t}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="lg:col-span-7">
                        <div
                            data-testid="photo-estimator-card"
                            className="glass shadow-glow-cyan p-6 md:p-8 rounded-3xl relative overflow-hidden"
                        >
                            {!photo ? (
                                <div
                                    data-testid="photo-estimator-dropzone"
                                    className="border-2 border-dashed border-electric/40 bg-carbon2/60 transition-all p-10 text-center rounded-2xl"
                                >
                                    <div className="mx-auto w-16 h-16 rounded-2xl bg-electric/10 flex items-center justify-center">
                                        <Camera
                                            size={30}
                                            strokeWidth={1.5}
                                            className="text-electric"
                                        />
                                    </div>
                                    <div className="mt-4 font-display text-2xl text-white">
                                        Add a photo of the damage
                                    </div>
                                    <p className="text-slate-400 text-sm mt-2">
                                        Take one now, or upload one you already
                                        have — JPG, PNG, WEBP up to 6 MB
                                    </p>
                                    <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                                        <label
                                            data-testid="photo-estimator-camera"
                                            className="w-full sm:w-auto cursor-pointer px-6 py-3 text-sm font-bold uppercase tracking-wide text-white bg-safety hover:bg-safety-dark transition-all hover:-translate-y-0.5 shadow-glow-orange rounded-xl inline-flex items-center justify-center gap-2"
                                        >
                                            <Camera size={16} />
                                            Take a Photo
                                            <input
                                                type="file"
                                                accept="image/jpeg,image/png,image/webp"
                                                capture="environment"
                                                className="hidden"
                                                data-testid="photo-estimator-camera-input"
                                                onChange={(e) =>
                                                    handleFile(e.target.files?.[0])
                                                }
                                            />
                                        </label>
                                        <label
                                            data-testid="photo-estimator-upload"
                                            className="w-full sm:w-auto cursor-pointer px-6 py-3 text-sm font-bold uppercase tracking-wide text-white bg-white/5 border border-white/15 hover:border-electric/60 hover:bg-white/10 transition-all rounded-xl inline-flex items-center justify-center gap-2"
                                        >
                                            <Upload size={16} className="text-electric" />
                                            Upload a Photo
                                            <input
                                                ref={inputRef}
                                                type="file"
                                                accept="image/jpeg,image/png,image/webp"
                                                className="hidden"
                                                data-testid="photo-estimator-input"
                                                onChange={(e) =>
                                                    handleFile(e.target.files?.[0])
                                                }
                                            />
                                        </label>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-5">
                                    <div
                                        className={`relative ${loading ? "scanner" : ""}`}
                                    >
                                        <img
                                            src={photo.preview}
                                            alt="Damage"
                                            data-testid="photo-estimator-preview"
                                            className="w-full max-h-72 object-cover rounded-2xl border border-white/10 shadow-xl"
                                        />
                                        <button
                                            onClick={reset}
                                            data-testid="photo-estimator-remove"
                                            className="absolute top-2 right-2 glass hover:bg-safety hover:text-white p-2 transition-colors rounded-xl text-slate-200"
                                            aria-label="Remove photo"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>

                                    {!result && (
                                        <>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="text-xs uppercase tracking-[0.15em] text-slate-500 mb-1.5 block">
                                                        Vehicle type
                                                    </label>
                                                    <select
                                                        data-testid="photo-vehicle-select"
                                                        value={vehicleType}
                                                        onChange={(e) =>
                                                            setVehicleType(
                                                                e.target.value,
                                                            )
                                                        }
                                                        className="w-full h-11 bg-carbon2 border border-white/15 focus:border-electric focus:outline-none text-white px-3 text-sm rounded-xl"
                                                    >
                                                        {VEHICLES.map((v) => (
                                                            <option
                                                                key={v.key}
                                                                value={v.key}
                                                            >
                                                                {v.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="text-xs uppercase tracking-[0.15em] text-slate-500 mb-1.5 block">
                                                        Anything to add? (optional)
                                                    </label>
                                                    <input
                                                        data-testid="photo-notes-input"
                                                        value={notes}
                                                        onChange={(e) =>
                                                            setNotes(
                                                                e.target.value,
                                                            )
                                                        }
                                                        placeholder="e.g. happened last week, no rust"
                                                        className="w-full h-11 bg-carbon2 border border-white/15 focus:border-electric focus:outline-none text-white placeholder:text-slate-500 px-3 text-sm rounded-xl"
                                                    />
                                                </div>
                                            </div>
                                            <button
                                                onClick={run}
                                                disabled={loading}
                                                data-testid="photo-estimator-analyze"
                                                className="w-full h-12 bg-safety hover:bg-safety-dark disabled:opacity-60 text-white text-sm uppercase tracking-[0.15em] font-bold transition-all hover:-translate-y-0.5 shadow-glow-orange inline-flex items-center justify-center gap-2 rounded-xl"
                                            >
                                                {loading ? (
                                                    <>
                                                        <Loader2
                                                            size={16}
                                                            className="animate-spin"
                                                        />
                                                        Analyzing your photo...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Sparkles size={16} />
                                                        Get my AI estimate
                                                    </>
                                                )}
                                            </button>
                                        </>
                                    )}

                                    {result && (
                                        <div
                                            data-testid="photo-estimator-result"
                                            className="bg-gradient-to-br from-carbon2 to-navy text-white p-6 rounded-2xl shadow-glow-cyan border border-electric/15 relative overflow-hidden"
                                        >
                                            <div className="absolute -top-16 -right-16 w-56 h-56 bg-electric/10 rounded-full blur-3xl pointer-events-none" />
                                            <div className="relative">
                                            <div className="text-xs uppercase tracking-[0.2em] text-safety mb-3">
                                                AI Estimate
                                            </div>
                                            <div
                                                data-testid="photo-estimator-range"
                                                className="inline-flex items-baseline gap-2 bg-safety px-4 py-2 rounded-xl shadow-lg"
                                            >
                                                <span className="font-display font-semibold text-4xl md:text-5xl text-navy leading-none">
                                                    {formatPrice(result.low)}
                                                </span>
                                                <span className="text-slate-700">–</span>
                                                <span className="font-display font-semibold text-4xl md:text-5xl text-navy leading-none">
                                                    {formatPrice(result.high)}
                                                </span>
                                            </div>
                                            <div className="text-xs text-slate-400 mt-3 uppercase tracking-[0.15em]">
                                                Confidence: {result.confidence}
                                            </div>
                                            <div className="text-xs text-slate-500 mt-1.5">
                                                Indicative only · excludes GST
                                            </div>

                                            <p
                                                data-testid="photo-estimator-summary"
                                                className="mt-4 text-slate-200 leading-relaxed"
                                            >
                                                {result.summary}
                                            </p>

                                            <div className="mt-4 flex flex-wrap gap-2">
                                                {result.detected_services.map(
                                                    (s) => (
                                                        <span
                                                            key={s}
                                                            className="px-2.5 py-1 bg-white/10 border border-white/20 text-slate-200 text-xs rounded-full"
                                                        >
                                                            {serviceLabel(s)}
                                                        </span>
                                                    ),
                                                )}
                                                <span className="px-2.5 py-1 bg-white/10 border border-white/20 text-slate-200 text-xs rounded-full capitalize">
                                                    Size: {result.detected_damage_size}
                                                </span>
                                            </div>

                                            {result.recommendations?.length > 0 && (
                                                <ul className="mt-5 space-y-1.5 text-sm text-slate-200">
                                                    {result.recommendations.map(
                                                        (r, i) => (
                                                            <li
                                                                key={i}
                                                                className="flex gap-2"
                                                            >
                                                                <span className="text-safety mt-0.5">
                                                                    ✦
                                                                </span>
                                                                <span>{r}</span>
                                                            </li>
                                                        ),
                                                    )}
                                                </ul>
                                            )}

                                            <div className="mt-6 pt-5 border-t border-white/10 flex flex-col sm:flex-row gap-3">
                                                <button
                                                    onClick={startFullQuote}
                                                    data-testid="photo-estimator-continue"
                                                    className="flex-1 h-11 bg-safety hover:bg-safety-dark text-white text-sm uppercase tracking-[0.15em] font-bold transition-all hover:-translate-y-0.5 shadow-glow-orange inline-flex items-center justify-center gap-2 rounded-xl"
                                                >
                                                    Book this in
                                                    <ArrowRight size={16} />
                                                </button>
                                                <button
                                                    onClick={reset}
                                                    data-testid="photo-estimator-redo"
                                                    className="flex-1 h-11 bg-white/5 border border-white/20 text-slate-200 hover:bg-white/10 hover:border-safety/50 text-sm uppercase tracking-[0.15em] rounded-xl transition-colors"
                                                >
                                                    Try another photo
                                                </button>
                                            </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <p className="text-xs text-slate-500 mt-3 text-center">
                            Indicative estimate only — confirmed after we see
                            the car in person.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}
